/*
* Interface for the datahub api.
*/
import axios from 'axios'
import moment from 'moment-timezone'
import config from './config'
import tzshift from './tzshift'

const url = `${config.datahubBaseUrl}/Recreation/ParkFacilityReservations`
const baseParams = { token: 'SS6gN075sqdRNbE28r7f1ey5FHZNGQDKEzK9jAB4' }


/* Create a chunk of a query for filtering by time, given a time range. */
const timeFilter = (timeRange) => {
  return `(reservationBeginTime le '1970-01-01T${timeRange[1]}:00.000Z' and reservationEndTime ge '1970-01-01T${timeRange[0]}:00.000Z')`
}


/* Get full name of park from park code. If field code provided, return field name as well.*/
const getParkFromCode = (parkCode, fieldCode) => {
  let params = {
    ...baseParams,
    $top: 1,
    $select: 'locationName,facilityName,parkUrlText'
  }

  let criteria = [`facilityLocationCode eq '${parkCode}'`]
  if(fieldCode) criteria.push(`comboKeyCode eq '${fieldCode}'`)
  params.$filter = criteria.join(' and ')

  return axios.get(url, { params })
    .then(response => {
      let d = response.data
      if(!d.length)
        return null
      else if(fieldCode)
        return { locationName: d[0].locationName, facilityName: d[0].facilityName, parkUrlText: d[0].parkUrlText }
      else
        return { locationName: d[0].locationName, parkUrlText: d[0].parkUrlText }
    })
}


/* Get full list of parks with codes. */
const getParkList = () => {
  const params = {
    ...baseParams,
    $select: 'locationName,facilityLocationCode',
    $orderby: 'locationName',
    $top: 1000,
    $filter:
      ['calendarIncludeInd eq 1',
      "(reservationFacilityTypeCode eq 'DIAMD' or reservationFacilityTypeCode eq 'RECT')"
      ].join(' and '),
    distinct: true
  }

  return axios.get(url, { params })
    .then(response => {
      return response.data.filter(item => item.locationName.length).map(item => {
        return {text: item.locationName, value: item.facilityLocationCode}
      })
    })
}

/* Get full list of fields for the park matching given park code. */
const getFieldList = (parkCode) => {
  const params = {
    ...baseParams,
    $filter: [
      `facilityLocationCode eq '${parkCode}'`,
      "(reservationFacilityTypeCode eq 'DIAMD' or reservationFacilityTypeCode eq 'RECT')",
      "calendarIncludeInd eq 1"
    ].join(' and '),
    $select: 'facilityName,comboKeyCode',
    $orderby: 'facilityName',
    $top: 1000,
    distinct: true
  }

  return axios.get(url, { params })
    .then(response => {
      return response.data.filter(item => item.facilityName.length).map(item => {
        return {text: item.facilityName, value: item.comboKeyCode}
      })
    })
}


/*
* Get detailed data for all fields matching query, categorized by park.
*
* Entries in the database are by reservation, but we make a distinct
* query selecting only data fields for the park or field level, so each
* field with reservations matching the query comes up once and only once.
* The return is an object with park codes as keys, which relate to park
* objects that contain the park name, address, and a list of field objects.
*/
const getFieldsByPark = (filters) => {
  let params = {
    ...baseParams,
    $top: 10000,
    $orderby: 'locationName,facilityName,reservationBeginDtm desc',
    $select: [
      'locationName',
      'facilityName',
      'streetAddressText',
      'cityName',
      'zipCode',
      'reservationFacilityTypeCode',
      'featureCodeList',
      'lightedFieldInd',
      'fieldTurfTypeDsc',
      'fieldOperationalStatusDsc',
      'facilityLocationCode',
      'facilitySpaceCode',
      'comboKeyCode',
      'fieldUseTypeDsc'
    ].join(','),
    distinct: true
  }

  let criteria = [
    `reservationBeginDtm ge '${tzshift.format(filters.startDate)}'`,
    "(reservationFacilityTypeCode eq 'DIAMD' or reservationFacilityTypeCode eq 'RECT')",
    timeFilter(filters.timeRange)
  ]
  criteria.push(`calendarIncludeInd eq 1`)

  if(filters.parkCode)
    criteria.push(`facilityLocationCode eq '${filters.parkCode}'`)
  if(filters.purpose)
    criteria.push(`contains(reservationPurposeDsc, '${encodeURIComponent(filters.purpose)}')`)
  if(filters.reservee)
  criteria.push(`contains(customerName, '${encodeURIComponent(filters.reservee)}')`)
  if(filters.type)
    criteria.push(`reservationFacilityTypeCode eq '${filters.type}'`)
  if(filters.lights)
    criteria.push(`lightedFieldInd eq ${filters.lights == 'Yes'}`)
  if(filters.status)
    criteria.push(`fieldOperationalStatusDsc eq '${filters.status}'`)
  if(filters.turfType)
    criteria.push(`fieldTurfTypeDsc eq '${filters.turfType}'`)
  if(filters.endDate)
    criteria.push(`reservationEndDtm lt '${tzshift.format(filters.endDate)}'`)
  params.$filter = criteria.join(' and ')

  return axios.get(url, { params })
    .then(response => {
      // Restructure data by park, duplicate field entries are dropped
      const structured = response.data.reduce((acc, item) => {
        if(!(item.facilityLocationCode in acc))
          acc[item.facilityLocationCode] = {}
        if(!(item.comboKeyCode in acc[item.facilityLocationCode]))
          acc[item.facilityLocationCode][item.comboKeyCode] = item
        return acc
      }, {})

      // We want Object.values in both cases here but IE can't handle it.
      // eslint-disable-next-line
      const processed = Object.entries(structured).map(([key, fieldObj]) => {
        // eslint-disable-next-line
        let fieldArr = Object.entries(fieldObj).map(([key, val]) => val)
        // Pull park-specific data from the first field
        // (it's the same across all fields) to build the park object
        let sample = fieldArr[0]
        return {
          locationName: sample.locationName,
          facilityLocationCode: sample.facilityLocationCode,
          streetAddressText: sample.streetAddressText,
          cityName: sample.cityName,
          zipCode: sample.zipCode,
          fields: fieldArr
        }
      })

      return processed
    })
}


/* Get all reservations matching query. */
const getReservations = (filters) => {
  let params = {
    ...baseParams,
    $top: 10000,
    $orderby: 'reservationBeginDtm, comboKeyCode'
  }

  const date = moment.tz(config.timezone).month(filters.month-1).year(filters.year)
  // Get data a week before and after given month to fill out calendar view.
  const startDate = moment(date).startOf('month')
  const endDate = moment(date).endOf('month')

  let criteria = [
    `reservationBeginDate ge '${tzshift.format(startDate)}'`,
    `reservationEndDate lt '${tzshift.format(endDate)}'`,
    `facilityLocationCode eq '${filters.parkCode}'`,
    "(reservationFacilityTypeCode eq 'DIAMD' or reservationFacilityTypeCode eq 'RECT')",
    "calendarIncludeInd eq 1",
    timeFilter(filters.timeRange)
  ]
  if(filters.fieldCode)
    criteria.push(`comboKeyCode eq '${filters.fieldCode}'`)
  if(filters.purpose)
    criteria.push(`contains(reservationPurposeDsc, '${encodeURIComponent(filters.purpose)}')`)
  if(filters.reservee)
  criteria.push(`contains(customerName, '${encodeURIComponent(filters.reservee)}')`)
  params.$filter = criteria.join(' and ')

  return axios.get(url, { params })
    .then(response => {
      // Fix timezone data for all returned reservations.
      return response.data.map(item => {
        [
          'reservationBeginTime',
          'reservationBeginDtm',
          'reservationEndTime',
          'reservationEndDtm'
        ].forEach(field => {
          item[field] = tzshift.parse(item[field]).format()
        })

        return item
      })
    }).then(data => {
      // Arrange by date for calendar view.
      const cal = data.reduce((acc, item) => {
        const itemDate = moment(item.reservationBeginDate).utc().format('YYYY-MM-DD')
        if(itemDate in acc)
          acc[itemDate].push(item)
        else
          acc[itemDate] = [item]
        return acc
      }, {})

      // Filter out data outside of the month for list view.
      const list = data.filter(item => {
        return moment(item.reservationBeginDtm).format('M') == filters.month
      })

      const scope = filters.fieldCode ? 'field' : 'park'

      return {cal, list, scope}
    })
}


/* Get all reservations matching query. for export */
const getExportReservations = (filters) => {
  let params = {
    ...baseParams,
    $top: 10000,
    $orderby: 'reservationBeginDtm'
  }

  let criteria = [
    `reservationBeginDtm ge '${tzshift.format(filters.startDate)}'`,
    `facilityLocationCode eq '${filters.parkCode}'`,
    "(reservationFacilityTypeCode eq 'DIAMD' or reservationFacilityTypeCode eq 'RECT')",
    "calendarIncludeInd eq 1",
    timeFilter(filters.timeRange)
  ]
  if(filters.fieldCode)
    criteria.push(`comboKeyCode eq '${filters.fieldCode}'`)
  if(filters.purpose)
    criteria.push(`contains(reservationPurposeDsc, '${encodeURIComponent(filters.purpose)}')`)
  if(filters.reservee)
  criteria.push(`contains(customerName, '${encodeURIComponent(filters.reservee)}')`)
  if(filters.endDate)
    criteria.push(`reservationEndDtm lt '${tzshift.format(filters.endDate)}'`)
  params.$filter = criteria.join(' and ')

  return axios.get(url, { params })
    .then(response => {
      // Fix timezone data for all returned reservations.
      return response.data.map(item => {
        [
          'reservationBeginTime',
          'reservationBeginDtm',
          'reservationEndTime',
          'reservationEndDtm'
        ].forEach(field => {
          item[field] = tzshift.parse(item[field]).format()
        })

        return item
      })
    }).then(data => {
      const list = data
      const scope = filters.fieldCode ? 'field' : 'park'

      return {list, scope}
    })
}


export default {
  getParkFromCode,
  getParkList,
  getFieldList,
  getFieldsByPark,
  getReservations,
  getExportReservations
}